import { Button } from '@/components/ui/button';
import { 
  Users, 
  Target, 
  Heart,
  BarChart3,
  Layers,
  Dumbbell,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const BOOKING_URL = "https://link.sweatsetter.com/widget/booking/0bvqp14e0y6pNvAdcqsM";

const programs = [
  {
    icon: Users,
    title: 'Large Group Training',
    subtitle: 'Coach-Led. High-Energy. Team-Driven.',
    description: 'Our large group training combines strength, conditioning, functional fitness, and accountability in a motivating team environment. Workouts are designed for all fitness levels and can be scaled to your ability.',
    benefits: [
      'structure and variety',
      'team energy and accountability',
      'indoor and outdoor workouts',
      'a challenging but supportive environment'
    ]
  },
  {
    icon: Layers,
    title: 'Semi-Private Training',
    subtitle: 'More Coaching. More Personalization.',
    description: 'Semi-private training gives you the support of a coach and the energy of a small group. It is ideal for people who want more individual attention while still training with others.',
    benefits: [
      'more personalized coaching',
      'flexible scheduling',
      'goal-based programming',
      'a smaller, more focused setting'
    ]
  },
  {
    icon: Target,
    title: 'Personal Training',
    subtitle: 'Fully Personalized Coaching',
    description: 'One-on-one personal training is built around your goals, movement ability, injury history, schedule, and pace. This is the most customized path we offer.',
    benefits: [
      'maximum individual attention',
      'a custom plan',
      'support returning from injury or long breaks',
      'schedule flexibility'
    ]
  },
  {
    icon: Heart,
    title: 'Nutrition Coaching',
    subtitle: 'Results Outside the Gym Matter Too',
    description: 'Training is only part of the equation. Our nutrition coaching helps you build better habits, fuel your workouts, improve body composition, and create a more sustainable lifestyle.',
    benefits: [
      'help with weight loss or body composition',
      'practical guidance, not fad diets',
      'accountability with daily habits',
      'better energy and recovery'
    ]
  },
  {
    icon: Dumbbell,
    title: 'HYROX Training',
    subtitle: 'Train With an Official HYROX Affiliate',
    description: 'As an official HYROX affiliate, X-Team helps you train with purpose — whether you want to complete your first race, improve your time, or build hybrid fitness.',
    benefits: [
      'race-specific conditioning',
      'structured endurance and strength training',
      'a motivating team environment',
      'preparation for HYROX events and performance goals'
    ]
  },
  {
    icon: BarChart3,
    title: 'Assessments & Progress Tracking',
    subtitle: 'What Gets Measured Gets Managed',
    description: 'We use assessments and body composition tools to give you real insight into your progress. Instead of guessing, you will have data that helps show where you started and how far you have come.',
    benefits: [
      'body composition analysis',
      'baseline performance tracking',
      'progress re-testing',
      'goal-based check-ins'
    ]
  }
];

const comparisonData = [
  { option: 'Large Group', best: 'Best for energy, variety, and accountability' },
  { option: 'Semi-Private', best: 'Best for more coaching in a small group' },
  { option: 'Personal Training', best: 'Best for maximum customization' },
  { option: 'HYROX', best: 'Best for performance and race prep' },
  { option: 'Nutrition', best: 'Best for stronger body-composition results' }
];

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/br1bereq_bear-crawl.jpg";

export default function Programs() {
  return (
    <div data-testid="programs-page">
      {/* Hero Section - Full Width Image */}
      <section 
        className="relative flex items-center justify-center"
        style={{ height: '735px', marginTop: '0' }}
        data-testid="programs-hero"
        aria-label="Programs hero"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${HERO_IMAGE})`,
            top: '135px',
            height: '600px'
          }}
        >
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 container-custom text-center" style={{ marginTop: '135px' }}>
          <div className="max-w-3xl mx-auto">
            {/* Text overlay box with semi-transparent background */}
            <div className="bg-black/30 p-[50px] inline-block">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white mb-6">
                Programs Built for Real Results
              </h1>
              <p className="text-lg text-white/90 mb-8">
                Whether you want the energy of a team, the attention of a coach, or a performance-focused path like HYROX, X-Team offers training options designed to help you get stronger, leaner, healthier, and more consistent.
              </p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid="programs-hero-cta"
                  className="bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav uppercase tracking-widest px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  Book a Free Intro
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro/Reassurance */}
      <section className="py-12 bg-black" data-testid="programs-intro" aria-label="Programs introduction">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center flex flex-col justify-center">
            <h2 className="text-3xl sm:text-4xl font-heading text-white mb-6">
              You Don't Need to Know Exactly Where to Start
            </h2>
            <p className="text-lg text-white/90">
              That is our job. We work with adults of different ages, experience levels, and goals. Some want to lose weight. Some want to build strength. Some want more accountability. Some want to train for HYROX. We will help you choose the right starting point and progress from there.
            </p>
          </div>
        </div>
      </section>

      {/* Program Cards Grid */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="programs-grid" aria-label="Programs grid">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-4">
              Choose the Coaching Style That Fits You Best
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programs.map((program, i) => (
              <div 
                key={i} 
                className="bg-white rounded-none p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-2"
                data-testid={`program-detail-${i}`}
              >
                <div className="w-14 h-14 rounded-none bg-gray-100 flex items-center justify-center mb-6">
                  <program.icon className="w-7 h-7 text-[#FF5A3C]" />
                </div>
                <h3 className="font-heading text-2xl text-[#1A1A1A] mb-2">{program.title}</h3>
                <p className="text-[#FF5A3C] font-semibold text-sm uppercase tracking-wide mb-4">{program.subtitle}</p>
                <p className="text-gray-600 mb-6">{program.description}</p>
                <p className="font-semibold text-[#1A1A1A] mb-3">Best for people who want:</p>
                <ul className="space-y-2">
                  {program.benefits.map((benefit, j) => (
                    <li key={j} className="flex items-start gap-2 text-gray-600">
                      <CheckCircle className="w-5 h-5 text-[#FF5A3C] flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Guide */}
      <section className="section-padding bg-white" data-testid="programs-comparison" aria-label="Program comparison">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-heading text-[#1A1A1A] mb-4">
                Which Option is Right for You?
              </h2>
            </div>
            
            <div className="bg-[#F5F7FA] rounded-none p-8">
              {comparisonData.map((item, i) => (
                <div 
                  key={i}
                  className={`flex items-center justify-between py-4 ${i !== comparisonData.length - 1 ? 'border-b border-gray-200' : ''}`}
                >
                  <span className="font-semibold text-[#1A1A1A]">{item.option}</span>
                  <span className="text-gray-600 text-right">{item.best}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* 5 Photo Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-[22px] mt-16 px-4 lg:px-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/ay0h0di9_steering%20wheels.jpg"
              alt="Member doing steering wheels exercise"
              className="w-full h-[300px] object-cover rounded-2xl"
              loading="lazy"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/sw9gi0i8_monika%20pullups.jpg"
              alt="Members doing assisted pullups"
              className="w-full h-[300px] object-cover rounded-2xl"
              loading="lazy"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/rbxqjkii_mckinley%20curl.jpg"
              alt="Member doing dumbbell curls"
              className="w-full h-[300px] object-cover rounded-2xl"
              loading="lazy"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/9kbrge05_run.jpg"
              alt="Group running on trail"
              className="w-full h-[300px] object-cover rounded-2xl"
              loading="lazy"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/iqievc87_hack%20squats.jpg"
              alt="Members doing hack squats"
              className="w-full h-[300px] object-cover rounded-2xl col-span-2 md:col-span-1"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-gradient-to-br from-[#FF5A3C] to-[#E64526] text-white" data-testid="programs-final-cta" aria-label="Call to action">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading mb-6">
            Not Sure Where to Start?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Book a Free Intro and we will help you choose the right path based on your goals, fitness level, and schedule.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button 
              data-testid="programs-final-cta-btn"
              className="bg-white text-[#FF5A3C] hover:bg-gray-100 rounded-none font-nav uppercase tracking-widest px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              Book a Free Intro <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
